import React from 'react';
import styles from './styles.module.scss';

interface TwoColumnLayoutProps {
  /** Content for the left sidebar (320px on desktop) */
  sidebar: React.ReactNode;
  /** Main content area (flexible width) */
  children: React.ReactNode;
  /** Optional mobile filter component shown above content on mobile */
  mobileFilter?: React.ReactNode;
  /** Additional className for container */
  className?: string;
}

/**
 * TwoColumnLayout - Reusable two-column layout with sidebar
 *
 * Desktop: 320px sidebar | flexible main content
 * Mobile: Stacked layout (sidebar on top, then main content)
 *
 * Usage:
 * <TwoColumnLayout
 *   sidebar={<SidebarContent />}
 *   mobileFilter={<CompactSelectionList />}
 * >
 *   <MainContent />
 * </TwoColumnLayout>
 */
export const TwoColumnLayout: React.FC<TwoColumnLayoutProps> = ({
  sidebar,
  children,
  mobileFilter,
  className,
}) => {
  return (
    <div className={`${styles.container} ${className || ''}`}>
      {/* Mobile filter - shown above content on mobile only */}
      {mobileFilter && (
        <div className={styles.mobileFilter}>
          {mobileFilter}
        </div>
      )}

      {/* Left sidebar */}
      <aside className={mobileFilter ? styles.sidebarWithMobileFilter : styles.sidebar}>
        {mobileFilter ? (
          <div className={styles.sidebarContentHiddenOnMobile}>
            {sidebar}
          </div>
        ) : (
          sidebar
        )}
      </aside>

      {/* Main content area */}
      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
};

export default TwoColumnLayout;
